import React, {
  useEffect,
  useMemo,
  useState
} from "react";
import { Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import auth from "./Registrarse/firebase";
import {
  adminRequest,
  apiUrl,
  uploadWithProgress
} from "./mangaApi";
import "./MangaUpload.css";

const emptyManga = {
  title: "",
  type: "manga",
  author: "",
  artist: "",
  scanlationGroup: "",
  magazine: "",
  language: "Español",
  publicationGroup: "",
  synopsis: "",
  teamNote: "",
  chapterNumber: "1",
  chapterTitle: ""
};

const fileNumber = (name) =>
  Number(
    name.match(/\d+/g)?.at(-1) ||
      Number.MAX_SAFE_INTEGER
  );

const titleFromFileName = (name = "") =>
  name
    .replace(/\.zip$/i, "")
    .replace(/_+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/*
 * Convierte diferentes respuestas del servidor
 * en un arreglo seguro.
 *
 * Acepta respuestas como:
 * [...]
 * { mangas: [...] }
 * { items: [...] }
 * { data: [...] }
 * { data: { mangas: [...] } }
 */
const getArrayFromResponse = (response, key) => {
  const possibleArrays = [
    response,
    response?.[key],
    response?.items,
    response?.results,
    response?.data,
    response?.data?.[key],
    response?.data?.items,
    response?.data?.results
  ];

  return (
    possibleArrays.find((value) =>
      Array.isArray(value)
    ) || []
  );
};

function FilePicker({
  zipFile,
  setZipFile,
  pages,
  setPages,
  disabled,
  isNewManga = false
}) {
  const [mode, setMode] = useState("zip");

  const sortedPages = useMemo(
    () =>
      [...pages].sort(
        (a, b) =>
          fileNumber(a.name) -
          fileNumber(b.name)
      ),
    [pages]
  );

  const changeMode = (next) => {
    setMode(next);
    setZipFile(null);
    setPages([]);
  };

  return (
    <section className="upload-card upload-files">
      <div className="upload-section-heading">
        <div>
          <span className="upload-kicker">
            {isNewManga
              ? "01 — ARCHIVO ZIP"
              : "ARCHIVOS"}
          </span>

          <h2>
            {isNewManga
              ? "Sube el manga primero"
              : "Páginas del capítulo"}
          </h2>
        </div>

        <div
          className="upload-segmented"
          aria-label="Formato de carga"
        >
          <button
            type="button"
            className={
              mode === "zip" ? "active" : ""
            }
            onClick={() => changeMode("zip")}
          >
            ZIP
          </button>

          <button
            type="button"
            className={
              mode === "images" ? "active" : ""
            }
            onClick={() => changeMode("images")}
          >
            Imágenes
          </button>
        </div>
      </div>

      <label className="upload-dropzone">
        <span className="upload-drop-icon">
          ⇧
        </span>

        <strong>
          {mode === "zip"
            ? "Selecciona un archivo .zip"
            : "Selecciona todas las páginas"}
        </strong>

        <small>
          Usa nombres numéricos: 01, 02, 03…
          El servidor valida y convierte a WebP.
        </small>

        {mode === "zip" ? (
          <input
            type="file"
            accept=".zip,application/zip"
            disabled={disabled}
            onChange={(event) =>
              setZipFile(
                event.target.files?.[0] || null
              )
            }
          />
        ) : (
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/avif"
            multiple
            disabled={disabled}
            onChange={(event) =>
              setPages(
                Array.from(
                  event.target.files || []
                )
              )
            }
          />
        )}
      </label>

      {zipFile && (
        <div className="upload-file-summary">
          <strong>{zipFile.name}</strong>

          <span>
            {(zipFile.size / 1024 / 1024).toFixed(
              1
            )}{" "}
            MB
          </span>
        </div>
      )}

      {sortedPages.length > 0 && (
        <div className="upload-page-list">
          <strong>
            {sortedPages.length} páginas
          </strong>

          <span>
            {sortedPages
              .slice(0, 8)
              .map((file) => file.name)
              .join(" · ")}

            {sortedPages.length > 8 ? "…" : ""}
          </span>
        </div>
      )}
    </section>
  );
}

export default function MangaUpload() {
  const [tab, setTab] = useState("new");
  const [user, setUser] = useState(
    auth.currentUser
  );
  const [access, setAccess] =
    useState("checking");
  const [tags, setTags] = useState([]);
  const [tagSearch, setTagSearch] =
    useState("");
  const [selectedTags, setSelectedTags] =
    useState([]);
  const [manga, setManga] =
    useState(emptyManga);
  const [zipFile, setZipFile] =
    useState(null);
  const [pages, setPages] = useState([]);
  const [mangas, setMangas] = useState([]);
  const [mangaSearch, setMangaSearch] =
    useState("");
  const [selectedManga, setSelectedManga] =
    useState("");
  const [chapter, setChapter] = useState({
    chapterNumber: "",
    chapterTitle: ""
  });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] =
    useState(0);
  const [notice, setNotice] =
    useState(null);
  const [result, setResult] =
    useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      setUser
    );

    return unsubscribe;
  }, []);

  /*
   * Cargar etiquetas y garantizar que tags
   * siempre sea un arreglo.
   */
  useEffect(() => {
    fetch(apiUrl("/tags"))
      .then((response) =>
        response.ok
          ? response.json()
          : Promise.reject(
              new Error(
                "No se pudieron cargar las etiquetas."
              )
            )
      )
      .then((response) => {
        const tagList = getArrayFromResponse(
          response,
          "tags"
        );

        setTags(tagList);
      })
      .catch(() => {
        setTags([]);

        setNotice({
          type: "error",
          text: "No se pudieron cargar las etiquetas."
        });
      });
  }, []);

  /*
   * Verificar administrador y cargar mangas.
   * La respuesta se convierte siempre en arreglo.
   */
  useEffect(() => {
    if (!user) {
      setAccess("login");
      setMangas([]);
      return;
    }

    setAccess("checking");

    Promise.all([
      adminRequest("/admin/me"),
      adminRequest("/mangas")
    ])
      .then(([, mangaResponse]) => {
        const mangaList =
          getArrayFromResponse(
            mangaResponse,
            "mangas"
          );

        setMangas(mangaList);
        setAccess("allowed");
      })
      .catch((error) => {
        setMangas([]);
        setAccess("denied");

        setNotice({
          type: "error",
          text: error.message
        });
      });
  }, [user]);


  const updateManga = (field) => (event) => {
    setManga((current) => ({
      ...current,
      [field]: event.target.value
    }));
  };

  const selectZipFile = (file) => {
    setZipFile(file);

    if (file && tab === "new") {
      setManga((current) => ({
        ...current,
        title: titleFromFileName(file.name)
      }));
    }
  };

  /*
   * Filtros protegidos para que nunca llamen
   * .filter sobre un objeto.
   */
  const filteredTags = (
    Array.isArray(tags) ? tags : []
  ).filter((tag) =>
    String(tag?.name || "")
      .toLowerCase()
      .includes(tagSearch.toLowerCase())
  );

  const filteredMangas = (
    Array.isArray(mangas) ? mangas : []
  ).filter((item) =>
    String(item?.title || "")
      .toLowerCase()
      .includes(mangaSearch.toLowerCase())
  );

  const canSubmit =
    access === "allowed" && !busy;

  const toggleTag = (id) => {
    setSelectedTags((current) =>
      current.includes(id)
        ? current.filter(
            (item) => item !== id
          )
        : [...current, id]
    );
  };

  const resetFiles = () => {
    setZipFile(null);
    setPages([]);
  };

  const submit = async (event) => {
    event.preventDefault();
    setNotice(null);
    setResult(null);

    if (!zipFile && pages.length === 0) {
      setNotice({
        type: "error",
        text: "Selecciona un ZIP o imágenes del capítulo."
      });
      return;
    }

    if (
      tab === "new" &&
      selectedTags.length < 3
    ) {
      setNotice({
        type: "error",
        text: "Selecciona al menos 3 etiquetas."
      });
      return;
    }


    if (
      tab === "chapter" &&
      !selectedManga
    ) {
      setNotice({
        type: "error",
        text: "Selecciona el manga al que pertenece el capítulo."
      });
      return;
    }

    const formData = new FormData();

    const metadata =
      tab === "new"
        ? {
            ...manga,
            chapterNumber: "1",
            chapterTitle: "",
            tagIds: selectedTags
          }
        : chapter;

    formData.append(
      "metadata",
      JSON.stringify(metadata)
    );


    if (zipFile) {
      formData.append("zip", zipFile);
    }

    pages.forEach((file) => {
      formData.append("pages", file);
    });

    setBusy(true);
    setProgress(0);

    try {
      const endpoint =
        tab === "new"
          ? "/mangas"
          : `/mangas/${selectedManga}/chapters`;

      const response =
        await uploadWithProgress(
          endpoint,
          formData,
          setProgress
        );

      setProgress(100);
      setResult(response);

      setNotice({
        type: "success",
        text:
          response?.message ||
          "La carga terminó correctamente."
      });

      resetFiles();

      if (tab === "chapter") {
        setChapter({
          chapterNumber: "",
          chapterTitle: ""
        });
      }

      /*
       * Volver a cargar la lista y convertir
       * la respuesta en arreglo.
       */
      const refreshed =
        await adminRequest("/mangas");

      setMangas(
        getArrayFromResponse(
          refreshed,
          "mangas"
        )
      );
    } catch (error) {
      setNotice({
        type: "error",
        text:
          error.message ||
          "No se pudo completar la carga."
      });
    } finally {
      setBusy(false);
    }
  };

  const publishResult = async () => {
    if (
      !result?.mangaId ||
      !result?.chapterId
    ) {
      return;
    }

    setBusy(true);

    try {
      await adminRequest(
        `/mangas/${result.mangaId}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({
            status: "approved"
          })
        }
      );

      await adminRequest(
        `/chapters/${result.chapterId}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({
            status: "approved"
          })
        }
      );

      setResult((current) => ({
        ...current,
        status: "approved"
      }));

      setNotice({
        type: "success",
        text: "Manga y capítulo publicados correctamente."
      });
    } catch (error) {
      setNotice({
        type: "error",
        text: error.message
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="upload-page">
      <header className="upload-header">
        <Link
          to="/"
          className="upload-brand"
        >
          TMO<span>X</span>NIGHT
        </Link>

        <div>
          <span className="upload-status-dot" />
          Panel editorial
        </div>
      </header>

      <div className="upload-shell">
        <div className="upload-hero">
          <span className="upload-kicker">
            BIBLIOTECA DIGITAL
          </span>

          <h1>Subir mangas</h1>

          <p>
            Crea una obra nueva o incorpora un
            capítulo. Cada envío queda aislado
            por IDs únicos y pasa por validación
            antes de publicarse.
          </p>
        </div>

        {access === "login" && (
          <div className="upload-access">
            Debes{" "}
            <Link to="/login">
              iniciar sesión
            </Link>{" "}
            con tu cuenta administradora.
          </div>
        )}

        {access === "checking" && (
          <div className="upload-access">
            Verificando permisos…
          </div>
        )}

        {access === "denied" && (
          <div className="upload-access denied">
            Tu sesión no tiene permiso de subida.
            Agrega tu UID o correo en la
            configuración privada del servidor.
          </div>
        )}

        <div
          className="upload-tabs"
          role="tablist"
        >
          <button
            type="button"
            className={
              tab === "new" ? "active" : ""
            }
            onClick={() => setTab("new")}
          >
            Nueva obra
          </button>

          <button
            type="button"
            className={
              tab === "chapter"
                ? "active"
                : ""
            }
            onClick={() =>
              setTab("chapter")
            }
          >
            Agregar capítulo
          </button>
        </div>

        <form onSubmit={submit}>
          {tab === "new" && (
            <FilePicker
              zipFile={zipFile}
              setZipFile={selectZipFile}
              pages={pages}
              setPages={setPages}
              disabled={!canSubmit}
              isNewManga
            />
          )}

          {tab === "new" ? (
            <>
              <section className="upload-card">
                <div className="upload-section-heading">
                  <div>
                    <span className="upload-kicker">
                      02 — INFORMACIÓN
                    </span>

                    <h2>Datos de la obra</h2>
                  </div>
                </div>

                <div className="upload-grid">
                  <label className="span-2">
                    Título (se completa desde el ZIP)
                    <input
                      required
                      maxLength="180"
                      value={manga.title}
                      onChange={updateManga(
                        "title"
                      )}
                    />
                  </label>

                  <label>
                    Tipo
                    <select
                      value={manga.type}
                      onChange={updateManga(
                        "type"
                      )}
                    >
                      <option value="manga">
                        Manga
                      </option>
                      <option value="manhwa">
                        Manhwa
                      </option>
                      <option value="manhua">
                        Manhua
                      </option>
                      <option value="comic">
                        Cómic
                      </option>
                      <option value="doujinshi">
                        Doujinshi
                      </option>
                      <option value="other">
                        Otro
                      </option>
                    </select>
                  </label>

                  <label>
                    Idioma
                    <input
                      required
                      maxLength="80"
                      value={manga.language}
                      onChange={updateManga(
                        "language"
                      )}
                    />
                  </label>

                  <label>
                    Autor
                    <input
                      required
                      maxLength="180"
                      value={manga.author}
                      onChange={updateManga(
                        "author"
                      )}
                    />
                  </label>

                  <label>
                    Artista
                    <input
                      required
                      maxLength="180"
                      value={manga.artist}
                      onChange={updateManga(
                        "artist"
                      )}
                    />
                  </label>

                  <label>
                    Grupo de traducción
                    <input
                      maxLength="180"
                      value={
                        manga.scanlationGroup
                      }
                      onChange={updateManga(
                        "scanlationGroup"
                      )}
                    />
                  </label>

                  <label>
                    Revista
                    <input
                      maxLength="180"
                      value={manga.magazine}
                      onChange={updateManga(
                        "magazine"
                      )}
                    />
                  </label>

                  <label className="span-2">
                    Grupo de publicación
                    <input
                      maxLength="180"
                      value={
                        manga.publicationGroup
                      }
                      onChange={updateManga(
                        "publicationGroup"
                      )}
                    />
                  </label>

                  <label className="span-2">
                    Sinopsis
                    <textarea
                      required
                      maxLength="5000"
                      rows="5"
                      value={manga.synopsis}
                      onChange={updateManga(
                        "synopsis"
                      )}
                    />
                  </label>

                  <label className="span-2">
                    Nota del equipo
                    <textarea
                      maxLength="5000"
                      rows="3"
                      value={manga.teamNote}
                      onChange={updateManga(
                        "teamNote"
                      )}
                    />
                  </label>
                </div>
              </section>

              <section className="upload-card upload-cover-tags">
                <div>
                  <span className="upload-kicker">
                    03 — PORTADA AUTOMÁTICA
                  </span>

                  <h2>Imagen principal</h2>

                  <div className="upload-cover-picker upload-cover-auto">
                    <span>
                      Se usará la primera página
                      <br />
                      <small>
                        El servidor toma 01, 001 o la
                        página numérica más baja del ZIP.
                      </small>
                    </span>
                  </div>
                </div>

                <div>
                  <span className="upload-kicker">
                    03 — CLASIFICACIÓN
                  </span>

                  <h2>
                    Etiquetas{" "}
                    <small>
                      {selectedTags.length}/3
                      mínimo
                    </small>
                  </h2>

                  <input
                    className="upload-tag-search"
                    type="search"
                    placeholder="Buscar etiqueta…"
                    value={tagSearch}
                    onChange={(event) =>
                      setTagSearch(
                        event.target.value
                      )
                    }
                  />

                  <div className="upload-tags">
                    {filteredTags.map((tag) => (
                      <button
                        type="button"
                        key={tag.id}
                        className={
                          selectedTags.includes(
                            tag.id
                          )
                            ? "selected"
                            : ""
                        }
                        onClick={() =>
                          toggleTag(tag.id)
                        }
                      >
                        {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              </section>


            </>
          ) : (
            <section className="upload-card">
              <span className="upload-kicker">
                OBRA EXISTENTE
              </span>

              <h2>Destino del capítulo</h2>

              <div className="upload-grid">
                <label className="span-2">
                  Buscar manga
                  <input
                    type="search"
                    placeholder="Escribe el título…"
                    value={mangaSearch}
                    onChange={(event) =>
                      setMangaSearch(
                        event.target.value
                      )
                    }
                  />
                </label>

                <label className="span-2">
                  Manga
                  <select
                    required
                    value={selectedManga}
                    onChange={(event) =>
                      setSelectedManga(
                        event.target.value
                      )
                    }
                  >
                    <option value="">
                      Selecciona una obra
                    </option>

                    {filteredMangas.map(
                      (item) => (
                        <option
                          key={item.id}
                          value={item.id}
                        >
                          {item.title} ·{" "}
                          {item.status} ·{" "}
                          {item.chapter_count}{" "}
                          caps.
                        </option>
                      )
                    )}
                  </select>
                </label>

                <label>
                  Número del capítulo
                  <input
                    required
                    maxLength="40"
                    value={
                      chapter.chapterNumber
                    }
                    onChange={(event) =>
                      setChapter((current) => ({
                        ...current,
                        chapterNumber:
                          event.target.value
                      }))
                    }
                  />
                </label>

                <label>
                  Título opcional
                  <input
                    maxLength="180"
                    value={
                      chapter.chapterTitle
                    }
                    onChange={(event) =>
                      setChapter((current) => ({
                        ...current,
                        chapterTitle:
                          event.target.value
                      }))
                    }
                  />
                </label>
              </div>
            </section>
          )}

          {tab === "chapter" && (
            <FilePicker
              zipFile={zipFile}
              setZipFile={selectZipFile}
              pages={pages}
              setPages={setPages}
              disabled={!canSubmit}
            />
          )}

          {notice && (
            <div
              className={`upload-notice ${notice.type}`}
            >
              {notice.text}
            </div>
          )}

          {busy && (
            <div className="upload-progress">
              <span
                style={{
                  width: `${progress}%`
                }}
              />

              <strong>{progress}%</strong>
            </div>
          )}

          {result && (
            <div className="upload-result">
              <div>
                <span>ESTADO</span>

                <strong>
                  {result.status === "approved"
                    ? "Publicado"
                    : "Pendiente de aprobación"}
                </strong>

                <small>
                  {result.pageCount || 0} páginas
                  {result.mangaId
                    ? ` · Manga ${result.mangaId.slice(
                        0,
                        8
                      )}`
                    : ""}
                  {result.chapterId
                    ? ` · Capítulo ${result.chapterId.slice(
                        0,
                        8
                      )}`
                    : ""}
                </small>
              </div>

              {result.status === "approved" &&
              result.mangaId &&
              result.chapterId ? (
                <Link
                  to={`/lector/${result.mangaId}/${result.chapterId}/0`}
                >
                  Abrir lector →
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={publishResult}
                  disabled={
                    busy ||
                    !result.mangaId ||
                    !result.chapterId
                  }
                >
                  Aprobar y publicar
                </button>
              )}
            </div>
          )}

          <button
            className="upload-submit"
            type="submit"
            disabled={!canSubmit}
          >
            {busy
              ? "Procesando…"
              : tab === "new"
                ? "Enviar obra y capítulo"
                : "Enviar capítulo"}
          </button>

          <p className="upload-storage-note">
            Los archivos persisten en el volumen
            del VPS y no dentro del frontend ni
            de la imagen Docker.
          </p>
        </form>
      </div>
    </main>
  );
}